import React, { useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

function SubscriptionManagement() {
  const { subscription, getPlanDetails, cancelSubscription, loading } = useSubscription();
  const { currentUser } = useAuth();
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.')) {
      return;
    }

    setCanceling(true);
    setError('');
    setSuccess('');

    try {
      await cancelSubscription();
      setSuccess('Your subscription has been canceled. You will continue to have access until the end of your current billing period.');
    } catch (error) {
      setError(error.message || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-emerald-400';
      case 'trialing':
        return 'text-blue-400';
      case 'canceled':
        return 'text-red-400';
      case 'past_due':
        return 'text-yellow-400';
      default:
        return 'text-klorah-text-light';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-klorah-primary"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-semibold text-white mb-4">No Active Subscription</h3>
        <p className="text-klorah-text-light mb-6">
          You don't have an active subscription. Subscribe to access all features.
        </p>
        <a
          href="/dashboard"
          className="bg-klorah-primary text-white px-6 py-3 rounded-xl hover:bg-klorah-primary/80 transition-all"
        >
          View Plans
        </a>
      </div>
    );
  }

  const planDetails = getPlanDetails();

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-klorah-card border border-klorah-border rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-6">Subscription Management</h2>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/50 rounded-xl text-emerald-400">
            {success}
          </div>
        )}

        {/* Subscription Details */}
        <div className="space-y-6">
          {/* Plan Info */}
          <div className="bg-klorah-dark border border-klorah-border rounded-xl p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {planDetails?.planType === 'yearly' ? 'Yearly Plan' : 'Monthly Plan'}
                </h3>
                <p className="text-klorah-text-light">
                  ${planDetails?.planType === 'yearly' ? '30' : '40'}/month
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(planDetails?.status)}`}>
                {getStatusText(planDetails?.status)}
              </span>
            </div>

            {planDetails?.cancelAtPeriodEnd && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-4">
                <p className="text-yellow-400 text-sm">
                  Your subscription will be canceled on {formatDate(planDetails?.currentPeriodEnd)}.
                </p>
              </div>
            )}
          </div>

          {/* Billing Period */}
          <div className="bg-klorah-dark border border-klorah-border rounded-xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">Billing Period</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-klorah-text-light">Current period:</span>
                <span className="text-white">
                  {formatDate(planDetails?.currentPeriodStart)} - {formatDate(planDetails?.currentPeriodEnd)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-klorah-text-light">Next billing date:</span>
                <span className="text-white">{formatDate(planDetails?.currentPeriodEnd)}</span>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-klorah-dark border border-klorah-border rounded-xl p-6">
            <h4 className="text-lg font-semibold text-white mb-4">Account Information</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-klorah-text-light">Email:</span>
                <span className="text-white">{currentUser?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-klorah-text-light">Subscription ID:</span>
                <span className="text-white font-mono text-sm">{subscription.id}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {!planDetails?.cancelAtPeriodEnd && (
              <button
                onClick={handleCancelSubscription}
                disabled={canceling}
                className="w-full bg-red-500/20 text-red-400 border border-red-500/50 rounded-xl py-3 px-6 hover:bg-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {canceling ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400 mr-2"></div>
                    Canceling...
                  </div>
                ) : (
                  'Cancel Subscription'
                )}
              </button>
            )}

            <a
              href="https://billing.stripe.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-klorah-primary text-white rounded-xl py-3 px-6 text-center hover:bg-klorah-primary/80 transition-all"
            >
              Manage Billing
            </a>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-klorah-dark border border-klorah-border rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4">Need Help?</h4>
          <p className="text-klorah-text-light mb-4">
            If you have any questions about your subscription or need assistance, please contact our support team.
          </p>
          <div className="space-y-2">
            <a
              href="mailto:support@klorah.app"
              className="block text-klorah-primary hover:text-klorah-primary/80 transition-all"
            >
              support@klorah.app
            </a>
            <p className="text-sm text-klorah-text-light">
              We typically respond within 24 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionManagement; 